import type { WalletRow } from './rows.js';

/**
 * The colour each wallet is drawn in, wherever a chart has to tell wallets
 * apart — today the accounts page, where one account's balance is split into
 * the wallets that own it.
 *
 * Stored on the wallet as `color` when somebody has picked one. When nobody
 * has, a colour from this palette, so a chart is readable the day the field
 * appears rather than after every wallet has been visited — and nothing is
 * written on anybody's behalf to get there. The picker shows the colour in
 * force, so the first person to change it is choosing, not discovering.
 *
 * No red: red already means "below zero" on every chart in this app, and a
 * wallet drawn in it would read as one in trouble. Distinct in both themes,
 * and the order is the order wallets get them in, so the first few are the
 * most different from each other.
 */
export const WALLET_PALETTE: readonly string[] = [
  '#4e79a7', // blue
  '#f28e2b', // orange
  '#59a14f', // green
  '#b07aa1', // purple
  '#76b7b2', // teal
  '#edc948', // yellow
  '#ff9da7', // pink
  '#9c755f', // brown
];

/**
 * Money nobody has earmarked. Grey, because it is the absence of a wallet
 * (ADR-0023) and should read as the background the coloured parts sit on.
 */
export const UNASSIGNED_COLOUR = '#9aa0a6';

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * A stored colour, if it is one.
 *
 * Checked rather than trusted because it goes straight into an SVG `fill`
 * and a CSS custom property: the field is a free string in the manifest, the
 * picker only ever writes `#rrggbb`, and anything else — a hand-edited row,
 * an older client — is treated as "not set" rather than painted.
 */
export function validColour(value: string | null | undefined): string | undefined {
  const v = (value ?? '').trim();
  return HEX.test(v) ? v.toLowerCase() : undefined;
}

/**
 * Every wallet's colour.
 *
 * A wallet's own choice wins. The rest take palette colours in turn,
 * skipping any a chosen colour already uses, so two wallets only share one
 * when there are more wallets than palette — and even then the cycle starts
 * again rather than handing everyone the last entry.
 *
 * In the wallets' own order (`sortOrder`, then name), sorted here rather
 * than taken from the caller: the wallets page and the accounts page both
 * ask, and two callers passing two orders would draw one wallet in two
 * colours. The default is stable for as long as nobody reorders the wallets;
 * picking a colour is what pins it.
 */
export function walletColours(
  rows: readonly Pick<WalletRow, 'id' | 'color' | 'name' | 'sortOrder'>[],
): Map<string, string> {
  const wallets = [...rows].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
  );
  const out = new Map<string, string>();
  const taken = new Set<string>();
  for (const w of wallets) {
    const own = validColour(w.color);
    if (own) { out.set(w.id, own); taken.add(own); }
  }
  const free = WALLET_PALETTE.filter((c) => !taken.has(c));
  const pool = free.length > 0 ? free : WALLET_PALETTE;
  let next = 0;
  for (const w of wallets) {
    if (out.has(w.id)) continue;
    out.set(w.id, pool[next % pool.length]!);
    next += 1;
  }
  return out;
}
